interface OAuthPopupOptions {
  url: string;
  platform: string;
  width?: number;
  height?: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const openOAuthPopup = ({
  url,
  platform,
  width = 600,
  height = 700,
  onSuccess,
  onError,
}: OAuthPopupOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Calculate center position for popup
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      // Window features for centered popup
      const windowFeatures = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'toolbar=no',
        'location=no',
        'status=no',
        'menubar=no',
        'scrollbars=yes',
        'resizable=yes',
      ].join(',');

      // Open popup window
      const popupWindow = window.open(
        url,
        `oauth_${platform}`,
        windowFeatures
      );

      if (!popupWindow) {
        const error = 'Popup blocked. Please allow popups for this site.';
        onError?.(error);
        reject(new Error(error));
        return;
      }

      // Focus the popup
      popupWindow.focus();

      // Monitor popup closure
      const checkInterval = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(checkInterval);

          // Notify parent window of integration completion
          window.postMessage(
            {
              type: 'INTEGRATION_ACTIVATED',
              platform,
              timestamp: Date.now(),
            },
            window.location.origin
          );

          onSuccess?.();
          resolve();
        }
      }, 500);

      // Safety cleanup after 10 minutes
      setTimeout(() => {
        if (!popupWindow.closed) {
          clearInterval(checkInterval);
          popupWindow.close();
          const error = 'OAuth flow timed out';
          onError?.(error);
          reject(new Error(error));
        }
      }, 600000);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to open OAuth popup';
      onError?.(errorMessage);
      reject(error);
    }
  });
};

export const listenForOAuthComplete = (
  callback: (platform: string) => void
): (() => void) => {
  const handleMessage = (event: MessageEvent) => {
    // Verify message origin for security
    if (event.origin !== window.location.origin) {
      return;
    }

    // Handle integration activation message
    if (event.data?.type === 'INTEGRATION_ACTIVATED') {
      callback(event.data.platform);
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};

export const getIntegrationStatus = (
  integrations: any[] | any,
  platform: string
): { isActive: boolean; integration: any | null;} => {
  if (!Array.isArray(integrations)) {
    return {
      isActive: false,
      integration: null,
    };
  }

  const targetPlatform = platform.toLowerCase();
  const integration = integrations.find((int) => {
    const provider = int.provider?.toLowerCase();
    const state = int.state?.toLowerCase();

    const isPlatformMatch =
      provider === targetPlatform ||
      (targetPlatform === 'instagram' &&
        (provider === 'instagram' ||
          provider === 'instagram_direct'));
    const isActive = state === 'active' || state === 'ok';
    return isPlatformMatch && isActive;
  });

  return {
    isActive: Boolean(integration),
    integration: integration || null,
  };
};